// SCR-STAFF-850 (AP-INS-01؛ US-INS-001 تا 004): بیمه‌گران مرجع و محلی مرکز، و قرارداد نسخه‌دار هر بیمه‌گر با مبنای تعرفه، مهلت ارسال
// اسناد، مهلت پرداخت و الزامات اسناد. قرارداد **اثر گذشته‌نگر ندارد**: نسخه تازه از تاریخ مؤثر خود اعمال می‌شود و نسخه جاری روز پیش
// بسته می‌شود (BR-INS-002)، پس صورتحساب‌های گذشته دست‌نخورده می‌مانند.
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsContractTermsRequest,
  type InsContractVersionView,
  type InsContractView,
  type InsClaimSubmissionMethod,
  type InsInsurerKind,
  type InsInsurerView,
  type InsRequiredDocumentKind,
  type InsTariffBasis,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  SelectRHF,
  StatusChip,
  CheckboxRHF,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import {
  contractTone,
  DOCUMENT_KINDS,
  insuranceKeys,
  INSURER_KINDS,
  SUBMISSION_METHODS,
  TARIFF_BASES,
} from './common';

export const SCR_STAFF_850 = 'SCR-STAFF-850';

export function InsurersPage() {
  const { t } = useTranslation('ins');
  const access = useAccess();
  const write = useWriteAccess('ins.insurer.manage');
  const [tab, setTab] = useState<'insurers' | 'contracts'>('insurers');
  const [creating, setCreating] = useState(false);

  return (
    <ListPage
      screenId={SCR_STAFF_850}
      titleKey="ins:insurers.title"
      subtitleKey="ins:insurers.subtitle"
      actions={
        access.can('ins.insurer.manage') ? (
          <Button
            variant="contained"
            onClick={() => setCreating(true)}
            disabled={!write.canWrite}
            data-testid="ins-insurer-add"
          >
            {t('ins:insurers.add')}
          </Button>
        ) : undefined
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <Tabs value={tab} onChange={(_, value: 'insurers' | 'contracts') => setTab(value)}>
            <Tab value="insurers" label={t('ins:insurers.tab_insurers')} data-testid="ins-tab-insurers" />
            {access.can('ins.contract.manage') && (
              <Tab value="contracts" label={t('ins:insurers.tab_contracts')} data-testid="ins-tab-contracts" />
            )}
          </Tabs>
        </Stack>
      }
    >
      {tab === 'insurers' ? <InsurersTable /> : <ContractsTable />}
      {creating && <InsurerDialog onClose={() => setCreating(false)} />}
    </ListPage>
  );
}

function InsurersTable() {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('ins.insurer.manage');
  const [editing, setEditing] = useState<InsInsurerView | null>(null);
  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers')),
  });

  const columns: DataTableColumn<InsInsurerView>[] = [
    { id: 'code', headerKey: 'ins:insurers.columns.code', cell: (row) => row.code },
    { id: 'name', headerKey: 'ins:insurers.columns.name', cell: (row) => row.name },
    { id: 'kind', headerKey: 'ins:insurers.columns.kind', cell: (row) => t(`ins:insurer_kind.${row.kind}`) },
    {
      id: 'source',
      headerKey: 'ins:insurers.columns.source',
      cell: (row) => (
        <StatusChip
          tone={row.isReference ? 'confirmed' : 'inactive'}
          labelKey={row.isReference ? 'ins:insurers.reference' : 'ins:insurers.local'}
        />
      ),
    },
    {
      id: 'contract',
      headerKey: 'ins:insurers.columns.contract',
      cell: (row) => (
        <StatusChip
          tone={row.activeContractId ? 'active' : 'pending'}
          labelKey={row.activeContractId ? 'ins:insurers.has_contract' : 'ins:insurers.no_contract'}
        />
      ),
    },
    {
      id: 'plans',
      headerKey: 'ins:insurers.columns.plans',
      cell: (row) => faDigits(Number(row.planCount)),
      align: 'right',
    },
    {
      id: 'status',
      headerKey: 'ins:insurers.columns.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'ins:insurers.columns.actions',
      cell: (row) =>
        access.can('ins.insurer.manage') ? (
          <Button
            size="small"
            onClick={() => setEditing(row)}
            disabled={!write.canWrite}
            data-testid={`ins-insurer-edit-${row.insurerId}`}
          >
            {t('ins:insurers.edit')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <DataTable
        captionKey="ins:insurers.caption"
        columns={columns}
        rows={insurers.data?.items}
        getRowId={(row) => row.insurerId}
        isLoading={insurers.isPending}
        error={insurers.error}
        onRetry={() => void insurers.refetch()}
        emptyTitleKey="ins:insurers.empty"
      />
      {editing && <InsurerDialog insurer={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

const insurerSchema = z.object({
  code: zRequiredText(32),
  name: zRequiredText(200),
  kind: z.string(),
  referenceCode: z.string(),
  isActive: z.boolean(),
});

type InsurerForm = z.input<typeof insurerSchema>;

function InsurerDialog({ insurer, onClose }: { insurer?: InsInsurerView; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<InsurerForm>({
    resolver: zodResolver(insurerSchema),
    defaultValues: {
      code: insurer?.code ?? '',
      name: insurer?.name ?? '',
      kind: insurer?.kind ?? 'Basic',
      referenceCode: insurer?.referenceCode ?? '',
      isActive: insurer?.isActive ?? true,
    },
  });

  const save = useMutation({
    mutationFn: async (values: InsurerForm) =>
      insurer
        ? unwrap(
            tenantApi.PUT('/api/v1/insurance/insurers/{id}', {
              params: { path: { id: insurer.insurerId } },
              body: { name: values.name, isActive: values.isActive },
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/insurance/insurers', {
              headers: { 'Idempotency-Key': idempotency.current() },
              body: {
                code: values.code,
                name: values.name,
                kind: values.kind as InsInsurerKind,
                referenceCode: values.referenceCode.trim() ? values.referenceCode.trim() : null,
              },
            }),
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.insurers(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['code', 'name'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      titleKey={insurer ? 'ins:insurers.edit_title' : 'ins:insurers.add_title'}
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <TextFieldRHF
          control={form.control}
          name="code"
          labelKey="ins:insurers.fields.code"
          disabled={Boolean(insurer)}
          data-testid="ins-insurer-code"
        />
        <TextFieldRHF control={form.control} name="name" labelKey="ins:insurers.fields.name" data-testid="ins-insurer-name" />
        <SelectRHF
          control={form.control}
          name="kind"
          labelKey="ins:insurers.fields.kind"
          disabled={Boolean(insurer)}
          options={INSURER_KINDS.map((kind) => ({ value: kind, labelKey: `ins:insurer_kind.${kind}` }))}
          data-testid="ins-insurer-kind"
        />
        {!insurer && (
          <TextFieldRHF
            control={form.control}
            name="referenceCode"
            labelKey="ins:insurers.fields.reference_code"
            helperKey="ins:insurers.fields.reference_code_hint"
            data-testid="ins-insurer-reference"
          />
        )}
        {insurer && <CheckboxRHF control={form.control} name="isActive" labelKey="ins:insurers.fields.is_active" />}
      </Stack>
    </FormDialog>
  );
}

function ContractsTable() {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('ins.contract.manage');
  const [adding, setAdding] = useState<InsContractView | null>(null);
  const [creating, setCreating] = useState(false);
  const contracts = useQuery({
    queryKey: insuranceKeys.contracts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/contracts')),
  });

  const columns: DataTableColumn<InsContractView>[] = [
    { id: 'insurer', headerKey: 'ins:contracts.columns.insurer', cell: (row) => row.insurerName },
    {
      id: 'status',
      headerKey: 'ins:contracts.columns.status',
      cell: (row) => <StatusChip tone={contractTone(row.status)} labelKey={`ins:contract_status.${row.status}`} />,
    },
    {
      id: 'effective',
      headerKey: 'ins:contracts.columns.effective',
      cell: (row) => <EffectiveVersion versions={row.versions} />,
    },
    {
      id: 'versions',
      headerKey: 'ins:contracts.columns.versions',
      cell: (row) => faDigits(row.versions.length),
      align: 'right',
    },
    {
      id: 'actions',
      headerKey: 'ins:contracts.columns.actions',
      cell: (row) =>
        access.can('ins.contract.manage') ? (
          <Button
            size="small"
            onClick={() => setAdding(row)}
            disabled={!write.canWrite}
            data-testid={`ins-contract-version-${row.contractId}`}
          >
            {t('ins:contracts.add_version')}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        {access.can('ins.contract.manage') && (
          <Button variant="outlined" onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="ins-contract-add">
            {t('ins:contracts.add')}
          </Button>
        )}
      </Stack>
      <DataTable
        captionKey="ins:contracts.caption"
        columns={columns}
        rows={contracts.data}
        getRowId={(row) => row.contractId}
        isLoading={contracts.isPending}
        error={contracts.error}
        onRetry={() => void contracts.refetch()}
        emptyTitleKey="ins:contracts.empty"
      />
      {(adding || creating) && (
        <ContractDialog contract={adding ?? undefined} onClose={() => (adding ? setAdding(null) : setCreating(false))} />
      )}
    </>
  );
}

/** نسخه مؤثر امروز با بازه اعتبار و مبنای تعرفه (BR-INS-002). */
function EffectiveVersion({ versions }: { versions: readonly InsContractVersionView[] }) {
  const { t } = useTranslation('ins');
  const effective = versions.find((version) => version.isEffective);

  if (!effective) {
    return <Typography variant="body2">{t('ins:contracts.no_effective')}</Typography>;
  }

  return (
    <Stack spacing={0.25} data-testid={`ins-contract-effective-${effective.versionId}`}>
      <Typography variant="body2">
        {t('ins:contracts.version_label', { number: faDigits(Number(effective.versionNumber)) })}
      </Typography>
      <Typography variant="caption">
        <JalaliDate value={effective.effectiveFrom} />
        {effective.effectiveTo ? <> — <JalaliDate value={effective.effectiveTo} /></> : null}
      </Typography>
      <Typography variant="caption">{t(`ins:tariff_basis.${effective.tariffBasis}`)}</Typography>
    </Stack>
  );
}

/**
 * الزامات اسناد قرارداد (US-INS-003، BR-INS-003): انتخاب چندگانه با Checkbox، چون Design System انتخاب چندگانه ندارد. این الزامات در
 * کنترل کیفیت پیش از ارسال مطالبه (AP-INS-02) اعمال می‌شوند.
 */
function DocumentRequirements({ form }: { form: UseFormReturn<ContractForm> }) {
  const { t } = useTranslation('ins');
  const selected = useWatch({ control: form.control, name: 'documents' }) ?? [];

  return (
    <Stack spacing={0.5} data-testid="ins-contract-documents">
      <Typography variant="subtitle2">{t('ins:contracts.fields.documents')}</Typography>
      <Typography variant="caption">{t('ins:contracts.fields.documents_hint')}</Typography>
      {DOCUMENT_KINDS.map((kind) => (
        <FormControlLabel
          key={kind}
          control={
            <Checkbox
              checked={selected.includes(kind)}
              onChange={(event) =>
                form.setValue(
                  'documents',
                  event.target.checked ? [...selected, kind] : selected.filter((item) => item !== kind),
                  { shouldDirty: true },
                )
              }
              data-testid={`ins-contract-document-${kind}`}
            />
          }
          label={t(`ins:document_kind.${kind}`)}
        />
      ))}
    </Stack>
  );
}

const contractSchema = z
  .object({
    insurerId: z.string(),
    effectiveFrom: z.string().min(1, 'common:validation.required'),
    tariffBasis: z.string(),
    agreedPriceListId: z.string(),
    submissionDeadlineDays: z.string(),
    submissionMethod: z.string(),
    paymentTermDays: z.string(),
    centerCode: z.string(),
    contactName: z.string(),
    documents: z.array(z.string()),
  })
  .superRefine((form, ctx) => {
    if (!(Number(form.submissionDeadlineDays) >= 1 && Number(form.submissionDeadlineDays) <= 365)) {
      ctx.addIssue({ code: 'custom', path: ['submissionDeadlineDays'], message: 'ins:contracts.days_invalid' });
    }

    if (!(Number(form.paymentTermDays) >= 1 && Number(form.paymentTermDays) <= 365)) {
      ctx.addIssue({ code: 'custom', path: ['paymentTermDays'], message: 'ins:contracts.days_invalid' });
    }

    // مبنای تعرفه توافقی بدون فهرست قیمت معنا ندارد (AC-INS-002.1).
    if (form.tariffBasis === 'AgreedTariffList' && form.agreedPriceListId.trim() === '') {
      ctx.addIssue({ code: 'custom', path: ['agreedPriceListId'], message: 'common:validation.required' });
    }
  });

type ContractForm = z.input<typeof contractSchema>;

function ContractDialog({ contract, onClose }: { contract?: InsContractView; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers')),
    enabled: !contract,
  });
  const form = useForm<ContractForm>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      insurerId: '',
      effectiveFrom: '',
      tariffBasis: 'ApprovedTariff',
      agreedPriceListId: '',
      submissionDeadlineDays: '30',
      submissionMethod: 'FileExport',
      paymentTermDays: '60',
      centerCode: '',
      contactName: '',
      documents: [],
    },
  });

  const save = useMutation({
    mutationFn: async (values: ContractForm) => {
      const terms: InsContractTermsRequest = {
        effectiveFrom: values.effectiveFrom,
        effectiveTo: null,
        branchIds: [],
        tariffBasis: values.tariffBasis as InsTariffBasis,
        agreedPriceListId: values.agreedPriceListId ? values.agreedPriceListId : null,
        planIds: [],
        submissionDeadlineDays: Number(values.submissionDeadlineDays),
        submissionMethod: values.submissionMethod as InsClaimSubmissionMethod,
        paymentTermDays: Number(values.paymentTermDays),
        appealWindowDays: 30,
        batchPeriod: 'JalaliMonth',
        centerCode: values.centerCode.trim() ? values.centerCode.trim() : null,
        contactName: values.contactName.trim() ? values.contactName.trim() : null,
        documents: values.documents.map((kind) => ({
          categoryId: null,
          kind: kind as InsRequiredDocumentKind,
        })),
      };

      return contract
        ? unwrap(
            tenantApi.POST('/api/v1/insurance/contracts/{id}/versions', {
              params: { path: { id: contract.contractId } },
            headers: { 'Idempotency-Key': idempotency.current() },
              body: terms,
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/insurance/contracts', {
              headers: { 'Idempotency-Key': idempotency.current() },
              body: { insurerId: values.insurerId, terms },
            }),
          );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.contracts(session?.tenantId) });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.insurers(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['effectiveFrom', 'agreedPriceListId'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      titleKey={contract ? 'ins:contracts.add_version_title' : 'ins:contracts.add_title'}
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        {!contract && (
          <SelectRHF
            control={form.control}
            name="insurerId"
            labelKey="ins:contracts.fields.insurer"
            options={(insurers.data?.items ?? []).map((insurer) => ({ value: insurer.insurerId, label: insurer.name }))}
            data-testid="ins-contract-insurer"
          />
        )}
        <span data-testid="ins-contract-effective-from">
          <JalaliDatePickerRHF control={form.control} name="effectiveFrom" labelKey="ins:contracts.fields.effective_from" />
        </span>
        <SelectRHF
          control={form.control}
          name="tariffBasis"
          labelKey="ins:contracts.fields.tariff_basis"
          options={TARIFF_BASES.map((basis) => ({ value: basis, labelKey: `ins:tariff_basis.${basis}` }))}
          data-testid="ins-contract-basis"
        />
        <TextFieldRHF
          control={form.control}
          name="agreedPriceListId"
          labelKey="ins:contracts.fields.agreed_price_list"
          helperKey="ins:contracts.fields.agreed_price_list_hint"
          data-testid="ins-contract-price-list"
        />
        <TextFieldRHF
          control={form.control}
          name="submissionDeadlineDays"
          labelKey="ins:contracts.fields.submission_deadline"
          type="number"
          data-testid="ins-contract-deadline"
        />
        <SelectRHF
          control={form.control}
          name="submissionMethod"
          labelKey="ins:contracts.fields.submission_method"
          options={SUBMISSION_METHODS.map((method) => ({ value: method, labelKey: `ins:submission_method.${method}` }))}
          data-testid="ins-contract-method"
        />
        <TextFieldRHF
          control={form.control}
          name="paymentTermDays"
          labelKey="ins:contracts.fields.payment_term"
          type="number"
          data-testid="ins-contract-payment-term"
        />
        <TextFieldRHF control={form.control} name="centerCode" labelKey="ins:contracts.fields.center_code" data-testid="ins-contract-center" />
        <TextFieldRHF control={form.control} name="contactName" labelKey="ins:contracts.fields.contact" data-testid="ins-contract-contact" />
        <DocumentRequirements form={form} />
      </Stack>
    </FormDialog>
  );
}
